/**
 * Sending identities — the From/Reply-To/BCC envelope each product sends with.
 *
 * One block per product identity. Editing the From address invalidates the
 * provider-side verification, so the server reports `verificationCleared` and
 * the block asks for a re-verify. Verification itself needs provider
 * credentials, which are deployment secrets, so a missing one is reported with
 * the `wrangler secret put` name to run.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { emailProvider, senderIdentities, type SenderIdentity } from "../../lib/api";
import { Button, Input } from "@/ui";
import { emailProviderQueryKey, errorText } from "./EmailProviderSettings";

const identitiesQueryKey = ["sender-identities", "list"] as const;

/** Form fields: the identity's own keys plus the bulk columns that live under `stored`. */
type DraftKey = keyof SenderIdentity | "bulkFromAddress" | "bulkReplyTo" | "bulkUnsubscribeBaseUrl";

const csv = (v: string): string[] => v.split(",").map((s) => s.trim()).filter(Boolean);

function Field({ label, value, onChange, placeholder, hint }: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    /** Shown when the input is empty — the value the send path falls back to. */
    placeholder?: string;
    hint?: string;
}) {
    return (
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span className="muted" style={{ fontSize: 12 }}>{label}</span>
            <Input
                value={value}
                placeholder={placeholder}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            />
            {hint && <span className="muted" style={{ fontSize: 11 }}>{hint}</span>}
        </label>
    );
}

function IdentityBlock({ identity, canEdit }: { identity: SenderIdentity; canEdit: boolean }) {
    const queryClient = useQueryClient();
    // Only what the user typed lives here; everything else falls back to the
    // stored identity, so a refetch that changes untouched fields shows through.
    const [draft, setDraft] = useState<Partial<Record<DraftKey, string>>>({});
    const [notice, setNotice] = useState<string | null>(null);

    const val = (k: DraftKey, stored: string) => draft[k] ?? stored;
    const set = (k: DraftKey) => (v: string) => setDraft((d) => ({ ...d, [k]: v }));

    // Inputs are prefilled from the RAW row, not the resolved profile: the
    // resolved value for an unset field is a fallback (Reply-To = From, domain
    // lists = the From domain), and prefilling with it would pin it as a real
    // stored value the first time anyone saves. The fallback is the placeholder
    // instead, so "empty" keeps meaning "derive it".
    const fromAddress = val("fromAddress", identity.fromAddress);
    const fromName = val("fromName", identity.fromName);
    const replyTo = val("replyTo", identity.stored.replyTo ?? "");
    const bccAddress = val("bccAddress", identity.stored.bccAddress ?? "");
    const unsubscribeBaseUrl = val("unsubscribeBaseUrl", identity.stored.unsubscribeBaseUrl ?? "");
    const tracked = val("trackedDomains", identity.stored.trackedDomains.join(", "));
    const recipients = val("recipientDomains", identity.stored.recipientDomains.join(", "));
    const bulkFrom = val("bulkFromAddress", identity.stored.bulkFromAddress ?? "");
    const bulkReplyTo = val("bulkReplyTo", identity.stored.bulkReplyTo ?? "");
    const bulkUnsub = val("bulkUnsubscribeBaseUrl", identity.stored.bulkUnsubscribeBaseUrl ?? "");

    const save = useMutation({
        mutationFn: async () => {
            const res = await senderIdentities.update({
                productKey: identity.productKey,
                fromAddress: fromAddress.trim(),
                fromName: fromName.trim(),
                replyTo: replyTo.trim(),
                bccAddress: bccAddress.trim(),
                unsubscribeBaseUrl: unsubscribeBaseUrl.trim(),
                trackedDomains: csv(tracked),
                recipientDomains: csv(recipients),
                bulkFromAddress: bulkFrom.trim(),
                bulkReplyTo: bulkReplyTo.trim(),
                bulkUnsubscribeBaseUrl: bulkUnsub.trim(),
            });
            if (!res.ok) throw new Error(res.error);
            return res;
        },
        onSuccess: (res) => {
            setDraft({});
            setNotice(res.verificationCleared ? "Verification cleared — verify again" : "Saved");
            queryClient.invalidateQueries({ queryKey: identitiesQueryKey });
        },
        onError: (err: unknown) => setNotice(errorText(err)),
    });

    const verify = useMutation({
        mutationFn: () => senderIdentities.verify(identity.productKey),
        onSuccess: (res) => {
            setNotice(res.ok
                ? res.message
                : res.missingSecret
                    ? `${res.error} — set ${res.missingSecret} with wrangler secret put`
                    : res.error);
            queryClient.invalidateQueries({ queryKey: identitiesQueryKey });
            queryClient.invalidateQueries({ queryKey: emailProviderQueryKey });
        },
        onError: (err: unknown) => setNotice(errorText(err)),
    });

    const test = useMutation({
        mutationFn: () => emailProvider.test({ productKey: identity.productKey }),
        onSuccess: (res) => setNotice(res.ok ? "Test email sent" : res.error),
        onError: (err: unknown) => setNotice(errorText(err)),
    });

    const busy = save.isPending || verify.isPending || test.isPending;

    return (
        <div style={{ borderTop: "1px solid var(--border, #2222)", paddingTop: 12, marginTop: 12 }}>
            <div className="row" style={{ gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                <strong>{identity.name}</strong>
                <span className="muted" style={{ fontSize: 12 }}>{identity.productKey}</span>
                <span className="muted" style={{ fontSize: 12 }}>
                    {identity.verifiedAt
                        ? `Verified ${new Date(identity.verifiedAt).toLocaleDateString()}`
                        : "Not verified"}
                </span>
                {identity.envDefault && <span className="muted" style={{ fontSize: 12 }}>Env default</span>}
            </div>

            {!canEdit ? (
                <p className="muted" style={{ fontSize: 13, marginTop: 6, lineHeight: 1.6 }}>
                    {identity.fromName} &lt;{identity.fromAddress}&gt;
                    <br />Reply-To {identity.replyTo || "—"} · BCC {identity.bccAddress || "—"}
                    <br />Unsubscribe {identity.unsubscribeBaseUrl || "—"}
                    <br />Tracked {identity.trackedDomains.join(", ") || "—"}
                    {" · "}Recipients {identity.recipientDomains.join(", ") || "—"}
                    <br />Campaigns from {identity.bulk?.fromAddress ?? identity.fromAddress}
                </p>
            ) : (
                <>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 10, maxWidth: 420 }}>
                        <Field label="From name" value={fromName} onChange={set("fromName")} />
                        <Field label="From address" value={fromAddress} onChange={set("fromAddress")} />
                        <Field label="Reply-To" value={replyTo} onChange={set("replyTo")} placeholder={identity.replyTo} />
                        <Field label="BCC archive" value={bccAddress} onChange={set("bccAddress")} placeholder="Optional" />
                        <Field label="Unsubscribe base URL" value={unsubscribeBaseUrl} onChange={set("unsubscribeBaseUrl")} placeholder={identity.unsubscribeBaseUrl} />
                        <Field label="Tracked domains" value={tracked} onChange={set("trackedDomains")} placeholder={identity.trackedDomains.join(", ") || "Comma-separated"} />
                        <Field
                            label="Recipient domains (outbound only)"
                            value={recipients}
                            onChange={set("recipientDomains")}
                            placeholder={identity.recipientDomains.join(", ") || "Comma-separated"}
                            hint="Picks the product an outbound reply sends as. Inbound routing is unaffected — mail-in uses its own *_RECIPIENT_DOMAINS vars."
                        />

                        <strong style={{ fontSize: 13, marginTop: 8 }}>Bulk sender (campaigns)</strong>
                        <span className="muted" style={{ fontSize: 11, marginTop: -4 }}>
                            Campaigns send from here so a disposable domain absorbs any reputation hit.
                            Compose, replies and booking mail keep the From above. Leave blank to send campaigns from the From above.
                        </span>
                        <Field label="Bulk From address" value={bulkFrom} onChange={set("bulkFromAddress")} placeholder={identity.bulk?.fromAddress ?? "Optional"} />
                        <Field label="Bulk Reply-To" value={bulkReplyTo} onChange={set("bulkReplyTo")} placeholder={identity.bulk?.replyTo ?? "Defaults to bulk From"} />
                        <Field label="Bulk unsubscribe base URL" value={bulkUnsub} onChange={set("bulkUnsubscribeBaseUrl")} placeholder={identity.bulk?.unsubscribeBaseUrl ?? "Defaults to the unsubscribe URL above"} />
                    </div>

                    <div className="row" style={{ gap: 8, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
                        <Button variant="primary" disabled={busy} onClick={() => { setNotice(null); save.mutate(); }}>
                            {save.isPending ? "Saving…" : "Save"}
                        </Button>
                        <Button disabled={busy} onClick={() => { setNotice(null); verify.mutate(); }}>
                            {verify.isPending ? "Verifying…" : "Verify with provider"}
                        </Button>
                        <Button disabled={busy} onClick={() => { setNotice(null); test.mutate(); }}>
                            {test.isPending ? "Sending…" : "Send test"}
                        </Button>
                    </div>
                </>
            )}

            {notice && <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>{notice}</p>}
        </div>
    );
}

export default function SenderIdentitySettings() {
    const query = useQuery({ queryKey: identitiesQueryKey, queryFn: () => senderIdentities.list() });
    const data = query.data?.ok ? query.data : null;

    return (
        <section className="settings-card">
            <h3>Sending identities</h3>
            <p className="muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
                The From address, Reply-To and unsubscribe host each product sends with, plus an
                optional bulk sender for campaigns. Changing either From address clears its verification.
            </p>

            {query.isLoading && <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>Loading…</p>}
            {query.isError && <p className="danger" style={{ fontSize: 13, marginTop: 8 }}>{errorText(query.error)}</p>}
            {query.data && !query.data.ok && (
                <p className="danger" style={{ fontSize: 13, marginTop: 8 }}>{query.data.error}</p>
            )}

            {data?.identities.map((identity) => (
                <IdentityBlock key={identity.productKey} identity={identity} canEdit={data.canEdit} />
            ))}
        </section>
    );
}
